/**
 * Slim banner at the very top of the public proposal advertising the
 * files staff have attached to this share link (typically a completed
 * client cost grid). Hidden when there are no attachments so the
 * client never sees an empty placeholder.
 *
 * The fetch + download flow lives in `usePublicAttachments`; this
 * component is a pure presentational consumer driven by the props
 * passed in from `PublicProposal.jsx`. The same data also powers the
 * the client attachments list, so the client
 * has two equally-visible paths to the file (top banner + in-tab
 * card) - kills the "I can't find the cost grid" problem at the root.
 *
 * Tracking: every download dispatches a `download` analytics event
 * via the hook's `onDownloadEvent` callback so the staff Engagement
 * panel can render "ICON_template.xlsx - downloaded 3× by 2 viewers".
 */
import { Paperclip, Download, FileSpreadsheet, FileText, FileImage, File as FileIcon, Loader2 } from "lucide-react";

const fmtSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (ext) => {
  switch ((ext || "").toLowerCase()) {
    case "xlsx":
    case "xls":
    case "csv":
      return FileSpreadsheet;
    case "docx":
    case "doc":
    case "pdf":
    case "txt":
      return FileText;
    case "png":
    case "jpg":
    case "jpeg":
      return FileImage;
    default:
      return FileIcon;
  }
};


export default function AttachmentBanner({ items, downloadingId, downloadAttachment }) {
  // Don't render anything until the parent has loaded the list -
  // avoids a flash of empty banner on the first paint.
  if (items == null) return null;
  if (!items.length) return null;

  return (
    <div
      data-testid="attachment-banner"
      className="bg-[#1B4F8A] text-white px-4 sm:px-6 py-3 shadow-sm"
    >
      <div className="max-w-screen-xl mx-auto flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2 text-sm shrink-0">
          <Paperclip className="w-4 h-4 text-[#3EA8DC]" />
          <span className="font-medium">
            {items.length === 1
              ? "Your Medicys team has attached 1 file to this proposal"
              : `Your Medicys team has attached ${items.length} files to this proposal`}
          </span>
        </div>
        <ul className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
          {items.map((att) => {
            const Icon = iconFor(att.ext);
            const isLoading = downloadingId === att.id;
            return (
              <li key={att.id}>
                <button
                  type="button"
                  onClick={() => downloadAttachment(att)}
                  disabled={isLoading}
                  data-testid={`attachment-download-${att.id}`}
                  className="inline-flex items-center gap-1.5 bg-white text-[#1B4F8A] border border-white hover:bg-[#3EA8DC]/10 hover:border-[#3EA8DC] px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-60 shadow-sm"
                  title={`${att.filename} · ${fmtSize(att.size_bytes)}`}
                >
                  {isLoading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Icon className="w-3.5 h-3.5" />
                  )}
                  <span className="font-mono truncate max-w-[220px]">{att.filename}</span>
                  <span className="text-stone-400 text-[10px] hidden sm:inline">
                    {fmtSize(att.size_bytes)}
                  </span>
                  <Download className="w-3 h-3" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
